import { observer } from "mobx-react-lite";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useTran } from "@/hooks";
import { SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Compose a value the SelectField uses to identify both `sortBy` and `sortKey`
// in a single string. Metadata sorts have no key; cell sorts encode the dim.
const encodeSort = (by, key) => (by === "cell" ? `cell:${key}` : by);

const decodeSort = (composite) => {
  if (composite.startsWith("cell:")) {
    return { by: "cell", key: composite.slice("cell:".length) };
  }
  return { by: composite, key: "" };
};

const SortBySelector = observer(
  ({ view, sort, dimKeys, dimLabelFor, onChange, onToggleDirection }) => {
    const { t } = useTran();

    const options = [
      { value: "code", label: t("Code") },
      { value: "category", label: t("Category") },
      { value: "title", label: t("Title") },
      ...(view === "year" ? [{ value: "target", label: t("Target") }] : []),
      ...dimKeys.map((k) => ({
        value: encodeSort("cell", String(k)),
        label: dimLabelFor(k),
      })),
    ];

    const composite = encodeSort(sort.by, sort.key);
    const DirectionIcon = sort.direction === "desc" ? ArrowDown : ArrowUp;

    return (
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("Sort by")}
        </span>
        <SelectField
          key={`sort-${composite}`}
          options={options}
          value={composite}
          onValueChange={(v) => {
            const { by, key } = decodeSort(v);
            onChange({ by, key, direction: sort.direction });
          }}
          sortOptions={false}
          selectClassName="h-8 w-40 text-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onToggleDirection}
          className={cn("h-8 px-2", "text-xs")}
          title={
            sort.direction === "asc"
              ? t("Sort ascending — click for descending")
              : t("Sort descending — click for ascending")
          }
        >
          <DirectionIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  },
);

export default SortBySelector;
